import {
  CallOutCoverageData,
  CallOutShiftStub,
  Command,
  timeRangeDurationString,
} from '@nextforce/api'
import { Text, makeStyles } from '@rneui/themed'
import { t } from 'app/i18l'
import { font } from 'app/theme'
import React from 'react'
import { ScrollView, View } from 'react-native'
import { SafeAreaView } from 'react-native-safe-area-context'
import { InterestedCrewList } from 'shifts/screens/shift/views/manager_shift_details'
import { CalledOutShiftDetails } from './called_out_shift_details'
import { RecommendedUserList } from './recommended_user_list'

interface Props {
  callOutCoverageData?: CallOutCoverageData
  onButtonPress(command: Command, note?: string): Promise<void>
}

export function CallOutConverageView({
  callOutCoverageData,
  onButtonPress,
}: Props) {
  const styles = useStyles()

  if (!callOutCoverageData) {
    return null
  }

  return (
    <SafeAreaView edges={['left', 'right', 'bottom']} style={styles.root}>
      <ScrollView
        contentContainerStyle={styles.container}
        keyboardShouldPersistTaps={'handled'}
      >
        <View>
          <Text style={styles.primaryStatusMessage}>
            {callOutCoverageData.statusMessages.primary}
          </Text>
        </View>
        <Text style={styles.subText}>{t('callOutDetails')}</Text>
        <View style={styles.subContainer}>
          <CalledOutShiftDetails shift={callOutCoverageData.shift} />
          <Text style={[styles.subText, styles.coverCallOutText]}>
            {t('coverCallOut')}
          </Text>
          {callOutCoverageData.callOutShiftStubs.map(callOutShiftStub => (
            <CallOutShiftStubView
              key={callOutShiftStub.splitShift.id}
              callOutShiftStub={callOutShiftStub}
              onButtonPress={onButtonPress}
            />
          ))}
        </View>
      </ScrollView>
    </SafeAreaView>
  )
}

function CallOutShiftStubView({
  callOutShiftStub,
  onButtonPress,
}: {
  callOutShiftStub: CallOutShiftStub
  onButtonPress(command: Command, note?: string): Promise<void>
}) {
  const styles = useStyles()
  const hasEligibleOrInterestedCrew =
    callOutShiftStub.recommendedUsers?.length ||
    callOutShiftStub.interestedUsers?.length

  return (
    <View>
      <Text style={styles.subText}>
        {timeRangeDurationString(
          callOutShiftStub.splitShift.startTime,
          callOutShiftStub.splitShift.endTime,
          callOutShiftStub.splitShift.duration
        )}
      </Text>
      {hasEligibleOrInterestedCrew ? (
        <>
          <RecommendedUserList
            key={callOutShiftStub.splitShift.id}
            users={callOutShiftStub.recommendedUsers}
            onButtonPress={onButtonPress}
          />
          <InterestedCrewList
            candidates={callOutShiftStub.interestedUsers}
            onButtonPress={onButtonPress}
          />
        </>
      ) : (
        <Text style={styles.splitShiftNote}>{callOutShiftStub.note}</Text>
      )}
    </View>
  )
}

const useStyles = makeStyles(theme => ({
  root: {
    flex: 1,
  },
  container: {
    flexGrow: 1,
    flexDirection: 'column',
    justifyContent: 'space-between',
    backgroundColor: theme.colors?.grey5,
    paddingBottom: 30,
  },
  primaryStatusMessage: {
    fontSize: 16,
    fontFamily: font.bold,
    textAlign: 'justify',
    padding: 10,
    color: theme.colors?.black,
  },
  subContainer: {
    flex: 1,
    justifyContent: 'flex-start',
    backgroundColor: theme.colors?.grey5,
  },
  splitShiftNote: { padding: 10, backgroundColor: theme.colors.white },
  subText: { fontSize: 14, margin: 10 },
  coverCallOutText: { fontWeight: 'bold' },
}))
